import React, { useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid, makeStyles, Link, Theme } from '@material-ui/core';
import produce from 'immer';
import { Trans } from 'react-i18next';
import QRCode from 'qrcode.react';
import clsx from 'clsx';
import { modeUIContext, showSuccessNotification, DialogResponse, DialogResponseCode, showDialog, DialogType, setIsEditingForm,
    showErrorNotification, selectCache, setCache } from '@moderepo/mode-ui-state';
import { TFAActivationInfo,
    AgentPreferences,
    Agent,
    RitzAPI,
    ApiError,
    OrganizationPasswordPolicy,
    PasswordExpirationPolicy,
    RitzAppSettings }
    from '@moderepo/mode-internal-apis';
import { selectLoggedInAgent,
    fetchAgentProfile,
    updateAgentPersonalProfile,
    logoutAgent,
    selectProjectById,
    ritzDataContext,
    selectAppSettings,
    fetchCSAppSettings }
    from '@moderepo/mode-data-state-ritz';
import { SelectInputOption, MyAccountPasswordInfo, getLanguageByCode, Language, deleteLocalStorageItem,
    MyAccountPasswordInfoUpdatableInputs, EMAIL_VALIDATION_REGEX, capitalizeAll, getErrorMessage, GenericInfoComp,
    FieldInputType, Time, getThemeName, ModeThemeType, modeSupportedThemes } from '@moderepo/mode-app-components';
import { MyAgentAccountProfileInfoProps, MyAgentAccountProfileInfoUpdatableInputs, MyAgentAccountTFAInfo, MyAgentAccountTFAInfoUpdatableInputs }
    from '@moderepo/mode-app-components-internal';
import { useRitzTranslation } from '../../utils/customHooks';
import * as Constants from '../../utils/constants';
import { RouteHelper } from '../routes/RouteHelper';
import { getDateTimeDisplay } from '../containerUtils';
import { genericValidatePassword, PASSWORD_EXPIRED_ALERT_SEEN_CACHE_KEY } from '../../utils/utils';
import { getOrganization } from '../app/App';



const useStyle = makeStyles((theme: Theme) => {
    return {

        accountAlertMessage: {
            color                       : theme.palette.error.dark,
            fontSize                    : '16px',
            paddingBottom               : theme.spacing(2),
            [theme.breakpoints.up('md')]: {
                fontSize: '18px',
            },
        },


        twoFAStatusQRCodeContainer: {
            textAlign    : 'center',
            paddingBottom: theme.spacing(2),
        },

        twoFAInstructionTitle: {
            paddingBottom: theme.spacing(2),
            fontWeight   : 'bold',
            fontSize     : 'larger',
        },

        twoFAInstruction: {
            paddingBottom: theme.spacing(2),
        },

        twoFAInstructionTitleLink: {
            display   : 'inline',
            fontWeight: 'bold',
        },

        twoFAInstructionTitleSecret: {
            fontWeight: 'bold',
            color     : theme.palette.primary.main,
        },
    };
}, { name: 'MyAccountPage' });



/**
 * For personal info form
 */
interface PersonalInputErrors {
    name?: string | undefined;
    email?: string | undefined;
    confirmEmail?: string | undefined;
    language?: string | undefined;
}


/**
 * For change password form
 */
interface PasswordInputErrors {
    currentPassword?: string | undefined;
    newPassword?: string | undefined;
    confirmPassword?: string | undefined;
}


/**
 * For 2FA form
 */
interface TFAInputErrors {
    activateCode?: string | undefined;
}


enum EditingMode {
    PERSONAL_INFO = 'personal_info',
    PASSWORD = 'password',
    TWO_FACTOR_AUTH = '2fa',
}



export const MyAccountPage: React.FC = () => {
    const params = useParams();
    const projectId = params.projectId ? Number(params.projectId) : undefined;
    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const project = projectId ? selectProjectById(dataState, projectId) : undefined;
    const { trans } = useRitzTranslation(project);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const [editingMode, setEditingMode] = useState<EditingMode | undefined>(undefined);
    const classes = useStyle();
    const agentLanguage = loggedInAgent && loggedInAgent.preferences && loggedInAgent.preferences.locale
        ? getLanguageByCode(Constants.SUPPORTED_LANGUAGES, loggedInAgent.preferences.locale) : undefined;
    const agentTheme = loggedInAgent && loggedInAgent.preferences && loggedInAgent.preferences.theme
        ? loggedInAgent.preferences.theme : undefined;
    const appSettings = selectAppSettings(dataState) as RitzAppSettings;


    useEffect(() => {
        if (!appSettings) {
            dataDispatch(fetchCSAppSettings()).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [appSettings, dataDispatch]);


    // Get the organization from the data state and then get the password policy from the organization
    const organization = getOrganization();
    const passwordPolicy = useMemo((): OrganizationPasswordPolicy | undefined => {
        return organization?.authSchemes.passwordPolicy;
    }, [organization]);


    const [personalInputErrors, setPersonalInputErrors] = useState<PersonalInputErrors>({ });
    const [passwordInputErrors, setPasswordInputErrors] = useState<PasswordInputErrors>({ });
    const [tfaActivateInfo, setTfaActivateInfo] = useState<TFAActivationInfo>();
    const [tfaInputErrors, setTFAInputErrors] = useState<TFAInputErrors>({ });


    const navigate = useNavigate();
    const routeHelper = RouteHelper.getInstance(navigate);



    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean, mode: EditingMode | undefined) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setEditingMode(mode);
    }, [uiDispatch]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setPersonalInputErrors({ });
        setPasswordInputErrors({ });
        setTFAInputErrors({ });

        onChangeEditingMode(false, undefined);
    }, [onChangeEditingMode]);


    const onCancelEdit = useCallback(() => {
        closeEditing();
    }, [closeEditing]);



    /**
     * On user form input change, we will clear the error message for that field.
     */
    const onPersonalInfoFormInputChange = useCallback((fieldName: string) => {
        setPersonalInputErrors(produce(personalInputErrors, (draft: any) => {
            draft[fieldName] = null;
        }));
    }, [personalInputErrors]);


    /**
     * Handler for when the user click Save input from the My personal form
     */
    const onPersonalInfoFormSubmit = useCallback(async (inputs: MyAgentAccountProfileInfoUpdatableInputs): Promise<boolean> => {

        if (!loggedInAgent) {
            return Promise.resolve(false);
        }

        const nameInput = inputs.name;
        const emailInput = inputs.email;
        const languageInput = inputs.language;
        const themeInput = inputs.theme;


        // Validate inputs. If any input contains error, set it in the inputErrors
        const newInputErrors: PersonalInputErrors = {};

        if (!nameInput || nameInput.length === 0) {
            newInputErrors.name = 'error.input.name.empty';
        }

        if (!emailInput || emailInput.length === 0) {
            newInputErrors.email = 'error.input.email.empty';
        } else if (!EMAIL_VALIDATION_REGEX.test(emailInput)) {
            newInputErrors.email = 'error.input.email.invalid';
        }


        if (emailInput !== loggedInAgent.email) {
            // if email is changed, we need to validate confirmEmail as well
            const confirmEmailInput = inputs.confirmEmail;

            if (!confirmEmailInput || confirmEmailInput.length === 0) {
                newInputErrors.confirmEmail = 'error.input.confirm_email.empty';
            } else if (!EMAIL_VALIDATION_REGEX.test(confirmEmailInput)) {
                newInputErrors.confirmEmail = 'error.input.email.invalid';
            } else if (confirmEmailInput !== emailInput) {
                newInputErrors.confirmEmail = 'error.input.confirm_email.not_match';
            }
        }



        // Update the personalInfoInputErrors state with the errors if there are any
        setPersonalInputErrors(newInputErrors);

        if (!newInputErrors.name && !newInputErrors.email && !newInputErrors.confirmEmail) {
            // Input validated successfully, make API call to update agent profile
            const newName = nameInput !== loggedInAgent.name ? nameInput : undefined;
            const newEmail = emailInput !== loggedInAgent.email ? emailInput : undefined;

            let newAgentPreferences: AgentPreferences | undefined;

            // If locale changed, add locale to the modifiedData. There are a few ways locale can change.
            // 1- The agent DOES NOT have any preferences data and now want to set a locale
            // - The agent DOES have preferences data but the current locale is not the same as the newLocale, there are 3 cases
            //     2- The preferences data but DOES NOT have locale set and there is a newLocal
            //     3- The preferences data has locale set but newLocal is a different local
            //     4- The preferences data has locale set but newLocal is undefined which mean agent wants to unset/remove locale
            if (!loggedInAgent.preferences && (languageInput || themeInput)) {
                // Case 1 - agentPreferences does not exist yet so create a new one.
                newAgentPreferences = {
                    locale: languageInput,
                    theme : themeInput,
                } as AgentPreferences;
            } else if (loggedInAgent.preferences
                && (loggedInAgent.preferences.locale !== languageInput
                || loggedInAgent.preferences.theme !== themeInput)) {
                // Case 2, 3, 4 - agentPreferences already exist so copy the current agentPreferences and replace the locale value
                newAgentPreferences = {
                    ...loggedInAgent.preferences,
                    locale: languageInput || '',
                    theme : themeInput || '',
                } as AgentPreferences;
            }

            // Check if any of the value changed before making the API call. If nothing changed, there is no need to make API call.
            if (newName || newEmail || newAgentPreferences) {
                const continueUpdateAgentProfile = async () => {
                    await dataDispatch(updateAgentPersonalProfile(loggedInAgent.id, {
                        name       : newName,
                        email      : newEmail,
                        preferences: newAgentPreferences,
                    }, false));

                    if (newEmail) {
                        // Log the user out and take the user to the login page
                        try {
                            await dataDispatch(logoutAgent());
                        } catch (error) {
                            // ignore error
                        }
                        deleteLocalStorageItem(Constants.LS_KEY_LOGIN_INFO);
                        routeHelper.gotoLoginPage();
                        uiDispatch(showSuccessNotification('notif.account_updated_successfully'));
                    } else {
                        await dataDispatch(fetchAgentProfile(loggedInAgent.id));
                        uiDispatch(showSuccessNotification('notif.account_updated_successfully'));
                        closeEditing();
                    }
                };

                // If agent changed email, need to confirm if he want to continue because changing email will log the user out
                try {
                    if (newEmail) {
                        await uiDispatch(showDialog({
                            type          : DialogType.CONFIRM,
                            title         : 'dialog.confirm_change_agent_email.title',
                            message       : 'dialog.confirm_change_agent_email.message',
                            positiveButton: 'label.yes',
                            negativeButton: 'label.cancel',
                            onClose       : async (response?: DialogResponse): Promise<boolean> => {
                                if (response && response.code === DialogResponseCode.POSITIVE) {
                                    try {
                                        await continueUpdateAgentProfile();
                                    } catch (error) {
                                        return Promise.resolve(false);
                                    }
                                }
                                return Promise.resolve(false);
                            },
                        }));
                    } else {
                        continueUpdateAgentProfile();
                    }

                    return Promise.resolve(true);
                } catch (error) {
                    /* API call error */
                    return Promise.resolve(false);
                }
            } else {
                // nothing changed
                closeEditing();
                return Promise.resolve(true);
            }
        }

        return Promise.resolve(false);
    }, [dataDispatch, uiDispatch, routeHelper, closeEditing, loggedInAgent]);



    /**
     * On user form input change, we will clear the error message for that field.
     */
    const onPasswordFormInputChange = useCallback((fieldName: string) => {
        setPasswordInputErrors(produce(passwordInputErrors, (draft: any) => {
            draft[fieldName] = null;
        }));
    }, [passwordInputErrors]);


    /**
     * Handler for when the user click Save input from the user form
     */
    const onPasswordFormSubmit = useCallback(async (inputs: MyAccountPasswordInfoUpdatableInputs): Promise<boolean> => {
        if (!loggedInAgent) {
            return Promise.resolve(false);
        }

        // This shouldn't happen since the user should already be redirected to Not Authorized page. We just need an "undefined" check
        // here so that we don't have to do if in multiple places
        if (!passwordPolicy) {
            return Promise.resolve(false);
        }

        // Get input values
        const currentPasswordInput = inputs.currentPassword;
        const newPasswordInput = inputs.newPassword;
        const confirmPasswordInput = inputs.confirmPassword;


        // Validate inputs. If any input contains error, set it in the inputErrors
        const newInputErrors: PasswordInputErrors = { };

        // currentPassword is only required is the agent.missingPassword is false
        if (!loggedInAgent.missingPassword && (!currentPasswordInput || currentPasswordInput.length === 0)) {
            newInputErrors.currentPassword = 'error.input.current_password.empty';
        }

        // Validate newPassword and confirmPassword and make sure the password and confirm password are entered and they are the same
        if (newPasswordInput === currentPasswordInput) {
            newInputErrors.newPassword = 'error.input.new_password.the_same';
        } else {
            newInputErrors.newPassword = genericValidatePassword(newPasswordInput, passwordPolicy);
        }

        if (!confirmPasswordInput || confirmPasswordInput.length === 0) {
            newInputErrors.confirmPassword = 'error.input.confirm_new_password.empty';
        } else if (newPasswordInput !== confirmPasswordInput) {
            newInputErrors.confirmPassword = 'error.input.confirm_password.not_match';
        }


        // Update the personalInfoInputErrors state with the errors if there are any
        setPasswordInputErrors(newInputErrors);


        if (!newInputErrors.currentPassword && !newInputErrors.newPassword && !newInputErrors.confirmPassword
            && newPasswordInput) {

            // all input validated successfully
            try {
                await RitzAPI.getInstance().updateAgentPersonalPassword(
                    loggedInAgent.orgId, loggedInAgent.email, currentPasswordInput, newPasswordInput,
                );

                uiDispatch(showSuccessNotification('notif.password_changed_successfully'));

                // Log the user out and take the user to the login page
                deleteLocalStorageItem(Constants.LS_KEY_LOGIN_INFO);
                await dataDispatch(logoutAgent());

                routeHelper.gotoLoginPage();
                return Promise.resolve(true);
            } catch (error) {
                uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
            }
        }

        return Promise.resolve(false);
    }, [loggedInAgent, passwordPolicy, uiDispatch, dataDispatch, routeHelper]);


    /**
     * On user form input change, we will clear the error message for that field.
     */
    const onTFAFormInputChange = useCallback((fieldName: string) => {
        setTFAInputErrors(produce(tfaInputErrors, (draft: any) => {
            draft[fieldName] = null;
        }));
    }, [tfaInputErrors]);


    /**
     * Handler for when the user click Save input from the TFA activation form
     */
    const onTFAFormSubmit = useCallback(async (inputs: MyAgentAccountTFAInfoUpdatableInputs): Promise<boolean> => {

        if (!loggedInAgent) {
            return Promise.resolve(false);
        }

        // Get input values
        const activateCodeInput = inputs.activateCode;


        // Validate inputs. If any input contains error, set it in the inputErrors
        const newInputErrors: TFAInputErrors = { };

        if (!activateCodeInput || activateCodeInput.length === 0) {
            newInputErrors.activateCode = 'error.input.activate_2fa_code.empty';
        } else if (Number.isNaN(Number(activateCodeInput))) {
            newInputErrors.activateCode = 'error.input.activate_2fa_code.invalid';
        }


        // Update the personalInfoInputErrors state with the errors if there are any
        setTFAInputErrors(newInputErrors);


        if (!newInputErrors.activateCode && activateCodeInput) {

            // all input validated successfully
            try {
                await RitzAPI.getInstance().activate2FAComplete(Number(activateCodeInput));

                // Reload agent's profile
                await dataDispatch(fetchAgentProfile(loggedInAgent.id));

                uiDispatch(showSuccessNotification('notif.2fa_updated_successfully'));
                closeEditing();

                return Promise.resolve(true);
            } catch (error) {
                uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
                return Promise.resolve(false);
            }
        }

        return Promise.resolve(false);
    }, [dataDispatch, uiDispatch, loggedInAgent, closeEditing]);



    /**
     * On Activate 2FA button click. We will confirm the user password first before going through the activate flow
     */
    const onActivate2FA = useCallback((errorMessage?: string) => {
        uiDispatch(showDialog({
            type   : DialogType.INPUT,
            title  : 'dialog.activate_2fa.title',
            message: 'dialog.activate_2fa.confirm_password.message',
            input  : {
                type : 'password',
                name : 'label.password',
                error: errorMessage,
            },
            positiveButton: 'label.ok',
            negativeButton: 'label.cancel',
            onClose       : async (response: DialogResponse | undefined) => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    const password = response.input ? response.input.trim() : undefined;

                    if (!password) {
                        onActivate2FA('error.input.password.empty');
                    } else {
                        // password is valid, try activate 2FA
                        try {
                            const tfaInfo = await RitzAPI.getInstance().activate2FAStart(password);
                            setTfaActivateInfo(tfaInfo);
                            onChangeEditingMode(true, EditingMode.TWO_FACTOR_AUTH);
                        } catch (error) {
                            uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
                            onActivate2FA();
                        }
                    }
                }
            },
        }));
    }, [uiDispatch, onChangeEditingMode]);


    /**
     * Deactivate TFA. Need to confirm with the agent first and ask for a password
     */
    const onDeactivate2FA = useCallback((errorMessage?: string) => {
        if (loggedInAgent) {
            uiDispatch(showDialog({
                type   : DialogType.INPUT,
                title  : 'dialog.confirm_deactivate_2fa.title',
                message: 'dialog.confirm_deactivate_2fa.message',
                input  : {
                    type : 'password',
                    name : 'label.password',
                    error: errorMessage,
                },
                positiveButton: 'label.deactivate',
                negativeButton: 'label.cancel',
                onClose       : async (response?: DialogResponse): Promise<void> => {
                    if (response && response.code === DialogResponseCode.POSITIVE) {
                        const password = response.input ? response.input.trim() : undefined;

                        if (!password) {
                            onDeactivate2FA('error.input.password.empty');
                        } else {

                            try {
                                await RitzAPI.getInstance().deactivate2FA(password);

                                // Reload agent's profile
                                await dataDispatch(fetchAgentProfile(loggedInAgent.id));

                                uiDispatch(showSuccessNotification('notif.2fa_updated_successfully'));
                                closeEditing();
                            } catch (error) {
                                uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
                                onDeactivate2FA();
                            }
                        }
                    }
                },
            }));
        }
    }, [dataDispatch, uiDispatch, closeEditing, loggedInAgent]);


    const createTFAInstruction = useCallback(() => {
        return (
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <div className={classes.twoFAInstructionTitle}>
                        {trans('page.my_account.2fa_section.instruction.step_1')}
                    </div>
                    <div className={classes.twoFAInstruction}>
                        <Trans i18nKey="page.my_account.2fa_section.instruction.install_app">
                            <Link
                                className={classes.twoFAInstructionTitleLink}
                                href={trans('link.google_authenticator_android_app')}
                                target="blank"
                            />
                            <Link
                                className={classes.twoFAInstructionTitleLink}
                                href={trans('link.google_authenticator_ios_app')}
                                target="blank"
                            />
                            <Link
                                className={classes.twoFAInstructionTitleLink}
                                href={trans('link.duo')}
                                target="blank"
                            />
                            <Link
                                className={classes.twoFAInstructionTitleLink}
                                href={trans('link.authy')}
                                target="blank"
                            />
                        </Trans>
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <div className={classes.twoFAInstructionTitle}>
                        {trans('page.my_account.2fa_section.instruction.step_2')}
                    </div>
                    <div className={classes.twoFAInstruction}>
                        <Trans i18nKey="page.my_account.2fa_section.instruction.scan_code">
                            <Link
                                className={classes.twoFAInstructionTitleLink}
                                href={tfaActivateInfo ? tfaActivateInfo.auth2FUrl : ''}
                                target="blank"
                            />
                        </Trans>
                    </div>
                    <div className={classes.twoFAStatusQRCodeContainer}>
                        <QRCode value={tfaActivateInfo ? tfaActivateInfo.auth2FUrl : ''} />
                    </div>
                    <div className={classes.twoFAInstruction}>
                        <Trans i18nKey="page.my_account.2fa_section.instruction.manual_enter_code">
                            <span className={classes.twoFAInstructionTitleSecret}>
                                {{ secret: tfaActivateInfo ? tfaActivateInfo.auth2FSecret : '' }}
                            </span>
                        </Trans>
                    </div>
                </Grid>

                <Grid item xs={12}>
                    <div className={classes.twoFAInstructionTitle}>
                        {trans('page.my_account.2fa_section.instruction.step_3')}
                    </div>
                    <div className={classes.twoFAInstruction}>
                        {trans('page.my_account.2fa_section.instruction.verify_code')}
                    </div>
                </Grid>
            </Grid>
        );
    }, [classes, tfaActivateInfo, trans]);


    /**
     * Check if the agent's password expired. The agent's profile contains a date of when the agent changed their password, passwordUpdateTime,
     * so we can use that info + the organization's password policy's maxAge to figure out if the user password expired.
     */
    const passwordExpiredMessage = useMemo((): string | undefined => {
        // NOTE: passwordPolicy.maxAge is OPTIONAL. If it is undefined, null, or have value 0 then it means password DON'T expire.
        if (passwordPolicy && passwordPolicy.maxAge && passwordPolicy.maxAge > 0 && loggedInAgent.passwordUpdateTime !== undefined) {

            // maxAge is in DAYS, we need to convert maxAge to milliseconds
            const maxAgeInMS = passwordPolicy.maxAge * Time.DAY_IN_MS;
            const passwordUpdatedTS = Date.parse(loggedInAgent.passwordUpdateTime);
            const now = Date.now();

            if (passwordUpdatedTS + maxAgeInMS < now) {
                // Password has expired. Now we need to determine which message to show based on the password expiration policy
                // There are 2 policies, WARN and BLOCK. WARN is less strict so the alert message will only suggest the
                // user to change password but doesn't enforce it. BLOCK is strict so the alert message will be more stern
                if (passwordPolicy.expirationPolicy === PasswordExpirationPolicy.WARN) {
                    return 'page.my_account.password_expired_warning_message';
                }
                return 'page.my_account.password_expired_blocked_message';
            }
        }

        return undefined;
    }, [loggedInAgent.passwordUpdateTime, passwordPolicy]);



    /**
     * Get the expired password alert message seen state where we keep track of whether the user seen the alert
     * NOTE: because the same user can be an agent on many different organization and each organization has different password policy
     * therefore this state contains a flag for EACH organization. It is not just 1 true/false value but it will be a MAP of
     * orgId => true/false
     */
    const expirePasswordAlertSeenState = selectCache(uiState, PASSWORD_EXPIRED_ALERT_SEEN_CACHE_KEY);
    useEffect(() => {
        // If there is a passwordExpired alert message BUT we have NOT mark that the user seen it, mark it as seen and then update the state
        if (organization && passwordExpiredMessage && !expirePasswordAlertSeenState?.[organization.id]) {
            // NOTE: expirePasswordAlertSeenState can be undefined if this is the first time
            const updatedState = expirePasswordAlertSeenState ?? {};

            // add the flag for the current organization
            updatedState[organization.id] = true;

            // Dispatch an action to update the state
            uiDispatch(setCache(PASSWORD_EXPIRED_ALERT_SEEN_CACHE_KEY, updatedState));
        }
    }, [expirePasswordAlertSeenState, organization, passwordExpiredMessage, uiDispatch]);


    return (
        <div className="page-main-content">

            {passwordExpiredMessage && (
                <div className={classes.accountAlertMessage}>
                    {trans(passwordExpiredMessage)}
                </div>
            )}

            {loggedInAgent && !loggedInAgent.verified && (
                <div className={classes.accountAlertMessage}>
                    {trans('page.my_account.not_verified_message')}
                </div>
            )}

            <div className="title-bar">
                <div className="title-bar-item title-text">
                    <div className="page-title">
                        {trans('page.title.my_account')}
                    </div>
                    <div className="page-subtitle">
                        {trans('page.subtitle.my_account')}
                    </div>
                </div>
            </div>


            {loggedInAgent !== undefined && appSettings && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={12} lg={4} xl={6}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} xl={passwordPolicy ? 6 : 12}>
                                <GenericInfoComp<MyAgentAccountProfileInfoProps>
                                    className={clsx('match-height', editingMode === EditingMode.PERSONAL_INFO && 'editing')}
                                    isEditing={editingMode === EditingMode.PERSONAL_INFO}
                                    title={capitalizeAll(trans('label.personal_info'))}
                                    icon="person"
                                    fields={{
                                        id: {
                                            label       : capitalizeAll(trans('label.id')),
                                            displayValue: loggedInAgent.id.toString(),
                                            value       : loggedInAgent.id,
                                        },
                                        name: {
                                            label       : capitalizeAll(trans('label.name')),
                                            displayValue: loggedInAgent.name ? loggedInAgent.name : capitalizeAll(trans('label.none')),
                                            value       : loggedInAgent.name,
                                            editable    : true,
                                            error       : personalInputErrors.name ? trans(personalInputErrors.name) : '',
                                        },
                                        email: {
                                            label       : capitalizeAll(trans('label.email')),
                                            displayValue: loggedInAgent.email ? loggedInAgent.email : capitalizeAll(trans('label.none')),
                                            value       : loggedInAgent.email,
                                            editable    : !loggedInAgent.missingPassword,
                                            error       : personalInputErrors.email ? trans(personalInputErrors.email) : '',
                                            inputType   : 'email',
                                        },
                                        confirmEmail: {
                                            label          : capitalizeAll(trans('label.confirm_email')),
                                            editable       : !loggedInAgent.missingPassword,
                                            error          : personalInputErrors.confirmEmail ? trans(personalInputErrors.confirmEmail) : '',
                                            editingModeOnly: true,
                                            hidden         : loggedInAgent.missingPassword,
                                            inputType      : 'email',
                                        },
                                        language: {
                                            label       : capitalizeAll(trans('label.language')),
                                            displayValue: agentLanguage ? trans(agentLanguage.name) : capitalizeAll(trans('label.none')),
                                            value       : agentLanguage ? agentLanguage.locale : undefined,
                                            controlType : FieldInputType.SELECT,
                                            options     : [{
                                                label: capitalizeAll(trans('label.none')),
                                                value: '',
                                            },
                                            ...Constants.SUPPORTED_LANGUAGES.map((language: Language): SelectInputOption<string> => {
                                                return {
                                                    label: trans(language.name),
                                                    value: language.locale,
                                                };
                                            })],
                                            editable: true,
                                            error   : personalInputErrors.language ? trans(personalInputErrors.language) : '',
                                        },
                                        theme: {
                                            label       : capitalizeAll(trans('label.theme')),
                                            displayValue: agentTheme
                                                ? capitalizeAll(trans(getThemeName(agentTheme)))
                                                : capitalizeAll(trans('theme.default')),
                                            value      : agentTheme || undefined,
                                            controlType: FieldInputType.SELECT,
                                            options    : [{
                                                label: capitalizeAll(trans(getThemeName(ModeThemeType.DEFAULT))),
                                                value: modeSupportedThemes[ModeThemeType.DEFAULT].type,
                                            } as SelectInputOption<string>,
                                            ...appSettings.supportedThemes.map((type: string): SelectInputOption<string> => {
                                                return {
                                                    label: capitalizeAll(trans(getThemeName(type))),
                                                    value: modeSupportedThemes[type].type,
                                                };
                                            })],
                                            editable: true,
                                        },
                                        creationTime: {
                                            label       : capitalizeAll(trans('label.date_joined')),
                                            displayValue: getDateTimeDisplay(loggedInAgent.creationTime, trans),
                                            value       : loggedInAgent.creationTime,
                                        },
                                    }}
                                    actions={{
                                        startEdit: {
                                            onClick: () => {
                                                onChangeEditingMode(true, EditingMode.PERSONAL_INFO);
                                            },
                                        },
                                        cancelEdit: {
                                            label  : capitalizeAll(trans('label.cancel')),
                                            onClick: onCancelEdit,
                                        },
                                        saveUpdate: {
                                            label  : capitalizeAll(trans('label.save')),
                                            onClick: onPersonalInfoFormSubmit,
                                        },
                                    }}
                                    onFieldClicked={() => {
                                        onChangeEditingMode(true, EditingMode.PERSONAL_INFO);
                                    }}
                                    onFormInputChange={onPersonalInfoFormInputChange}
                                />
                            </Grid>

                            {/* Show Password panel only if the organization has "passwordPolicy", the agent can login with email/password */}
                            {passwordPolicy && (
                                <Grid item xs={12} xl={6}>
                                    <MyAccountPasswordInfo
                                        className={clsx('match-height', editingMode === EditingMode.PASSWORD && 'editing')}
                                        isEditing={editingMode === EditingMode.PASSWORD}
                                        title={capitalizeAll(trans('label.password'))}
                                        icon="lock"
                                        fields={{
                                            currentPassword: !loggedInAgent.missingPassword
                                                ? {
                                                    label      : capitalizeAll(trans('label.current_password')),
                                                    placeholder: editingMode && editingMode === EditingMode.PASSWORD ? '' : '********',
                                                    editable   : true,
                                                    error      : passwordInputErrors.currentPassword
                                                        ? trans(passwordInputErrors.currentPassword) : '',
                                                } : undefined,
                                            newPassword: {
                                                label: capitalizeAll(trans(
                                                    loggedInAgent.missingPassword ? 'label.password' : 'label.new_password',
                                                )),
                                                placeholder: editingMode && editingMode === EditingMode.PASSWORD ? '' : '********',
                                                editable   : true,
                                                error      : passwordInputErrors.newPassword
                                                    ? trans(passwordInputErrors.newPassword, { min_len: passwordPolicy.minLength })
                                                    : '',
                                                editingModeOnly: !loggedInAgent.missingPassword,
                                            },
                                            confirmPassword: {
                                                label   : capitalizeAll(trans('label.confirm_password')),
                                                editable: true,
                                                error   : passwordInputErrors.confirmPassword
                                                    ? trans(passwordInputErrors.confirmPassword) : '',
                                                editingModeOnly: true,
                                            },
                                        }}
                                        texts={{
                                            setPasswordMessage: loggedInAgent.missingPassword
                                                ? trans('page.my_account.password_not_set_message')
                                                : undefined,
                                        }}
                                        actions={{
                                            startEdit: {
                                                onClick: () => {
                                                    onChangeEditingMode(true, EditingMode.PASSWORD);
                                                },
                                            },
                                            cancelEdit: {
                                                label  : capitalizeAll(trans('label.cancel')),
                                                onClick: onCancelEdit,
                                            },
                                            saveUpdate: {
                                                label  : capitalizeAll(trans('label.save')),
                                                onClick: onPasswordFormSubmit,
                                            },
                                        }}
                                        onFieldClicked={() => {
                                            onChangeEditingMode(true, EditingMode.PASSWORD);
                                        }}
                                        onFormInputChange={onPasswordFormInputChange}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    {/* Only enable 2FA if organization support login with email/password AND if the user has set a password */}
                    {passwordPolicy && !loggedInAgent.missingPassword && (
                        <Grid item xs={12} md={12} lg={8} xl={6}>
                            <MyAgentAccountTFAInfo
                                className={clsx('match-height', editingMode === EditingMode.TWO_FACTOR_AUTH && 'editing')}
                                isEditing={editingMode === EditingMode.TWO_FACTOR_AUTH}
                                title={capitalizeAll(trans('label.two_factor_authentication'))}
                                icon="security"
                                fields={{
                                    activateCode: {
                                        label   : capitalizeAll(trans('label.authentication_code')),
                                        editable: true,
                                        error   : tfaInputErrors.activateCode ? trans(tfaInputErrors.activateCode) : '',
                                    },
                                }}
                                isActivated={loggedInAgent.auth2FActivated}
                                isAllowed={!loggedInAgent.missingPassword}
                                tfaActivateInfo={tfaActivateInfo}
                                texts={{
                                    activateButton      : trans('label.activate_now'),
                                    deactivateButton    : trans('label.deactivate'),
                                    tfaEnabledMessage   : trans('page.my_account.2fa_section.enabled_message'),
                                    tfaNotEnabledMessage: trans('page.my_account.2fa_section.not_enabled_message'),
                                    tfaNotAllowedMessage: loggedInAgent.missingPassword
                                        ? trans('page.my_account.2fa_section.not_allowed_message')
                                        : undefined,
                                    instructions: createTFAInstruction(),
                                }}
                                onActivate2FAClicked={() => {
                                    onActivate2FA();
                                }}
                                onDeactivate2FAClicked={() => {
                                    onDeactivate2FA();
                                }}
                                actions={{
                                    cancelEdit: {
                                        label  : capitalizeAll(trans('label.cancel')),
                                        onClick: onCancelEdit,
                                    },
                                    saveUpdate: {
                                        label  : capitalizeAll(trans('label.save')),
                                        onClick: onTFAFormSubmit,
                                    },
                                }}
                                onFormInputChange={onTFAFormInputChange}
                            />
                        </Grid>
                    )}
                </Grid>
            )}

        </div>
    );
};
